import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import PageHero from '../../components/PageHero';
import { useAsync } from '../../lib/useAsync';
import { eventsApi } from '../../api/resources';
import { SkeletonRows, ErrorState, EmptyState } from '../../components/ui';
import { formatDateTime } from '../../lib/format';

export default function EventList() {
  const { data, loading, error, reload } = useAsync(() => eventsApi.list({ per_page: 12 }), []);

  return (
    <>
      <Seo title="Events" description="Upcoming events hosted by Zonta District 17 Area 1." />
      <PageHero
        eyebrow="Mark Your Calendar"
        title="Upcoming events"
        subtitle="Join us at our upcoming gatherings, fundraisers, and meetings."
        image="/img/community.jpg"
      />

      <section className="container-page py-12">
        {loading ? (
          // Events load as a single stack of full-width row cards
          // (`space-y-4`, 80px date block + text), not as a card grid — a
          // `SkeletonGrid` here collapsed a 3-across card wall into a list of
          // short rows the instant the data landed.
          <SkeletonRows count={6} variant="card" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data?.items?.length ? (
          <div className="space-y-4">
            {data.items.map((e) => (
              <Link
                key={e.id}
                to={`/events/${e.slug}`}
                className="card card-hover flex flex-col gap-4 p-5 sm:flex-row sm:items-center"
              >
                <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-xl bg-maroon-700 text-white">
                  <span className="text-xs uppercase">{new Date(e.starts_at).toLocaleString('en-US', { month: 'short' })}</span>
                  <span className="text-2xl font-bold leading-none">{new Date(e.starts_at).getDate() || '—'}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display text-lg font-bold text-maroon-700">{e.title}</h3>
                    {e.recaps_count > 0 && (
                      <span className="pill-gold" title="This event has a published recap">
                        Recap
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-ink-muted">
                    {formatDateTime(e.starts_at)} {e.location ? `· ${e.location}` : ''}
                  </p>
                  {e.excerpt && <p className="mt-2 text-sm text-ink-soft">{e.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState icon="🗓" title="No events scheduled" message="Check back soon for upcoming events." />
        )}
      </section>
    </>
  );
}
